import React, { useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@column/column-ui-kit';

import {
  EmptyState,
  PaginationWrapper,
  PaginationWrapperRefProps,
  PopoverFilterEntry,
  Section,
  TableColumn,
} from '~/components';
import { EventDetailModal } from '~/components/Dialog';
import { RelativeTime } from '~/elements';
import { EventRepository, parseEvent } from '~/repositories';
import { Inner } from '~/styles';
import { EventEvent } from '~/typings/API';
import { formatString } from '~/util';

import { CardTokenProps } from './CardToken';

interface Params {
  id: string;
}

const CARD_TOKEN_EVENT_TYPES = [
  'card_token.created',
  'card_token.activated',
  'card_token.suspended',
  'card_token.deactivated',
  'card_token.send_passcode',
  'card_token.pending_activation',
  'card_token.stepping_up',
  'card_token.pending_authentication',
  'card_token.activation_denied',
];

const EventMeta = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
  margin: -4px 0;
  gap: 4px;
`;

const EventType = styled.h4`
  flex: auto;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  margin: 0;
  flex-grow: 0;
  padding: 0;
  text-transform: capitalize;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

const Action = styled.div`
  text-align: right;
`;

const JSONButton = styled(Button)`
  &:hover {
    &:after {
      background: ${({ theme }) => theme.gray.blendToBackground(75)};
    }
  }
`;

const getCardTokenEventLabel = (eventType: string): string => {
  const parts = eventType.split('.');
  if (parts.length < 2) return formatString(eventType);

  const action = parts[parts.length - 1];
  switch (action) {
    case 'created':
      return 'Token Created';
    case 'activated':
      return 'Token Activated';
    case 'suspended':
      return 'Token Suspended';
    case 'deactivated':
      return 'Token Deactivated';
    case 'send_passcode':
      return 'Passcode Sent';
    case 'pending_activation':
      return 'Token Pending Activation';
    case 'stepping_up':
      return 'Token Stepping Up';
    case 'pending_authentication':
      return 'Token Pending Authentication';
    case 'activation_denied':
      return 'Token Activation Denied';
    default:
      return formatString(action);
  }
};

export const PageCardTokenEvents: React.FC = () => {
  const { id: cardTokenId } = useParams<keyof Params>() as Params;
  useOutletContext<CardTokenProps>();
  const paginationRef = useRef<PaginationWrapperRefProps>(null);
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState<any>(null);

  const handleOpenEventDetail = (data: any) => {
    setSelectedEventData(data);
    setIsEventDetailModalOpen(true);
  };

  const handleFetch = async (params: any) => {
    return EventRepository.getAll({
      ...params,
      objectIds: [cardTokenId],
    }).then((response) => {
      const entries = response.events.map((event: EventEvent) => ({
        ...parseEvent(event),
        action: (
          <Action>
            <JSONButton
              size="tiny"
              variant="muted"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEventDetail(event.data);
              }}
            >
              JSON
            </JSONButton>
          </Action>
        ),
      }));

      return { entries, hasMore: Boolean(response?.hasMore) };
    });
  };

  const filter: PopoverFilterEntry[] = [
    {
      id: 'types',
      label: 'Type',
      type: 'select',
      list: CARD_TOKEN_EVENT_TYPES.map((eventType) => ({
        label: getCardTokenEventLabel(eventType),
        value: eventType,
      })),
    },
    {
      id: 'created',
      label: 'Created',
      type: 'date',
    },
  ];

  const columns: TableColumn[] = [
    {
      Header: 'Event',
      accessor: 'eventType',
      width: 'minmax(200px, auto)',
      Cell: (current) => (
        <EventMeta>
          <EventType>{getCardTokenEventLabel(current.value)}</EventType>
          <EventDetail>
            <span>{current.value}</span>
          </EventDetail>
        </EventMeta>
      ),
    },
    {
      Header: 'Date',
      accessor: 'createdAt',
      width: 'minmax(140px, 180px)',
      Cell: (current) => <RelativeTime timestamp={current.value} />,
    },
    {
      Header: '',
      accessor: 'action',
      width: '80px',
    },
  ];

  return (
    <>
      <EventDetailModal
        isOpen={isEventDetailModalOpen}
        onClose={() => setIsEventDetailModalOpen(false)}
        data={selectedEventData}
      />

      <Inner p={0}>
        <Section headline="Events">
          <PaginationWrapper
            tableId="card-token-events"
            fetch={handleFetch}
            columns={columns}
            filter={filter}
            ref={paginationRef}
            empty={
              <EmptyState
                headline="No events yet"
                text="Events will appear here when card token status changes occur."
              />
            }
          />
        </Section>
      </Inner>
    </>
  );
};
